import useScrollPosition from "@react-hook/window-scroll";
import styled from "styled-components/macro";
import { useActiveLocale } from "../../hooks/useActiveLocale";
import {
  LOCALE_LABEL,
  SUPPORTED_LOCALES,
  SupportedLocale,
} from "../../constants/locales";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useLocationLinkProps } from "../../hooks/useLocationLinkProps";
import { isMobile } from "utils/userAgent";
import Logo from "assets/logo-x.png";
import Menu from "./components/Menu";
import MenuMobile from "./components/MenuMobile";

export enum FlyoutAlignment {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

const HeaderFrame = styled.div<{ showBackground: boolean }>`
  background: ${({ theme }) => theme.bg0};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  width: 100%;
  position: relative;
  padding: 1rem;
  z-index: 21;
  /* Background slide effect on scroll. */
  background-image: ${({ theme }) =>
    theme.bg0};
  background-position: ${({ showBackground }) =>
    showBackground ? "0 -100%" : "0 0"};
  background-size: 100% 200%;
  box-shadow: 0px 0px 0px 1px
    ${({ theme, showBackground }) =>
    showBackground ? theme.bg2 : "transparent;"};
  transition: background-position 0.1s, box-shadow 0.1s;
  background-blend-mode: hard-light;

`;

const HeaderControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-self: flex-end;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    margin-left: 1rem;
  `};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    margin-left: 2rem;
  `};
`;

const HeaderElement = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  &:not(:first-child) {
    margin-left: 0.5em;
  }

  /* addresses safari's lack of support for "gap" */
  & > *:not(:first-child) {
    margin-left: 8px;
  }

  ${({ theme }) => theme.mediaWidth.upToMedium`
    align-items: center;
  `};
`;

const InternalMenuItem = styled(Link)`
  flex: 1;
  padding: 0.5rem 0.5rem;
  color: ${({ theme }) => theme.text2};
  :hover {
    color: ${({ theme }) => theme.text1};
    cursor: pointer;
    text-decoration: none;
  }
  > svg {
    margin-right: 8px;
  }
`;

const InternalLinkMenuItem = styled(InternalMenuItem)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem 0.5rem;
  justify-content: space-between;
  text-decoration: none;
  :hover {
    background: ${({ theme }) => theme.bg1};
    cursor: pointer;
    text-decoration: none;
  }
`;

const MenuFlyout = styled.span<{ flyoutAlignment?: FlyoutAlignment }>`
  min-width: 150px;
  max-height: 350px;
  overflow: auto;
  background-color: ${({ theme }) => theme.bg0};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04),
    0px 16px 24px rgba(0, 0, 0, 0.04), 0px 24px 32px rgba(0, 0, 0, 0.01);
  border: 1px solid ${({ theme }) => theme.bg1};
  border-radius: 12px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  position: absolute;
  z-index: 99;
  padding: 12px;
  gap: 0.5rem;
  top: 80%;
`;

/* const MenuChain = styled.span<{ flyoutAlignment?: FlyoutAlignment }>`
  min-width: 100px;
  max-height: 350px;
  overflow: auto;
  background-color: ${({ theme }) => theme.bg2};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04),
    0px 16px 24px rgba(0, 0, 0, 0.04), 0px 24px 32px rgba(0, 0, 0, 0.01);
  border: 1px solid ${({ theme }) => theme.bg0};
  border-radius: 12px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  position: absolute;
  z-index: 99;
  padding: 12px;
  gap: 0.5rem;
  top: 80%;
`; */

const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: fit-content;
  padding-right: 5rem;
`;

const ToggleMenuItem = styled.button`
  background-color: transparent;
  margin: 0;
  padding: 0;
  border: none;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding: 0.2rem 0.2rem;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
  :hover {
    cursor: pointer;
    text-decoration: none;
  }

`;

const GlobeIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  background: ${({ theme }) => theme.text3};
  border-radius: 50%;
`;



function LanguageMenuItem({
  locale,
  active,
}: {
  locale: SupportedLocale;
  active: boolean;
}) {
  const { to, onClick } = useLocationLinkProps(locale);

  if (!to) return null;

  return (
    <InternalLinkMenuItem onClick={onClick} to={to}>
      <div style={{ color: active ? "#5B83B8" : "#000" }}>{LOCALE_LABEL[locale]}</div>
      {active && <FontAwesomeIcon icon={faCheck} width={16} color={active ? "#5B83B8" : "#000"} />}
    </InternalLinkMenuItem>
  );
}

export default function Header() {
  const activeLocale = useActiveLocale();
  const scrollY = useScrollPosition();

  const [open, setOpen] = useState(false);
  return (
    <HeaderFrame showBackground={scrollY > 45}>
      <a href="/">
        <img src={Logo} alt="logo" width={isMobile ? "180px" : "300px"} />
      </a>
      {!isMobile ? <Menu /> : <MenuMobile />}
      <HeaderControls>
        <Column>
          <HeaderElement onClick={() => setOpen(!open)}>
            <GlobeIcon>
              <FontAwesomeIcon icon={faGlobe} color="#fff" width={24} />
            </GlobeIcon>
          </HeaderElement>
          {open && (
            <MenuFlyout
              onMouseLeave={() => setOpen(false)}
              onClick={() => setOpen(!open)}
            >
              <ToggleMenuItem onClick={() => setOpen(false)}>
                <FontAwesomeIcon icon={faXmark} width={12} />
              </ToggleMenuItem>
              {SUPPORTED_LOCALES.map((locale, index) => (
                <LanguageMenuItem
                  locale={locale}
                  active={activeLocale === locale}
                  key={locale}
                />
              ))}
            </MenuFlyout>
          )}

        </Column>
      </HeaderControls>
    </HeaderFrame>
  );
}
